import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Calendar, Clock, MapPinned } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error("Failed to fetch event", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!event) {
    return <div className="text-center py-12">Event not found</div>;
  }

  const getEventTypeColor = (type) => {
    const colors = {
      "Career Fair": "bg-blue-100 text-blue-700",
      "Volunteer": "bg-green-100 text-green-700",
      "Workshop": "bg-purple-100 text-purple-700",
      "Networking": "bg-orange-100 text-orange-700"
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/events")}
        className="mb-4"
        data-testid="back-to-events-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Events
      </Button>

      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-teal-50">
          <div className="flex justify-between items-start gap-4">
            <div>
              <CardTitle className="text-3xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {event.name}
              </CardTitle>
            </div>
            <Badge className={`${getEventTypeColor(event.event_type)} text-base px-3 py-1`}>
              {event.event_type}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Location</p>
                <p className="text-gray-600">{event.city}, {event.county} County</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
              <div>
                <p className="font-medium text-gray-900">Date</p>
                <p className="text-gray-600">{format(new Date(event.date), "MMMM d, yyyy")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Time</p>
                <p className="text-gray-600">{event.time}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPinned className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
              <div>
                <p className="font-medium text-gray-900">Venue</p>
                <p className="text-gray-600">{event.location}</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Event Description</h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{event.description}</p>
          </div>

          <div className="border-t pt-6 bg-gradient-to-r from-orange-50 to-teal-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>How to Sign Up</h3>
            <p className="text-gray-700 leading-relaxed" data-testid="signup-info">{event.signup_info}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}